// =============================================================================
// Professional resume -- print to PDF
// =============================================================================
// Opens a clean, print-styled resume of the professional's profile and all
// their VERIFIED credentials in a new window and triggers the browser's print
// dialog, where the user chooses "Save as PDF". No third-party library -- the
// browser's own PDF engine does the rendering.

const TYPE_LABEL = {
  EDUCATION: "Education",
  WORK: "Work experience",
  OTHER: "Certifications & Other",
};

function esc(value) {
  return String(value === null || value === undefined ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function fmtMonthYear(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
}

function dateRange(c) {
  const s = fmtMonthYear(c.startDate);
  const e = c.isCurrent ? "Present" : fmtMonthYear(c.endDate);
  if (s && e) return `${s} - ${e}`;
  return s || e;
}

// What the resume shows of each credential's details: what it is, never
// pay, date of birth, ID numbers or internal ids (it used to print every
// stored key, salary included).
const RESUME_DETAILS = {
  EDUCATION: [
    ["fieldOfStudy", "Field of study"],
    ["grade", "Grade"],
    ["achievements", "Achievements"],
  ],
  WORK: [
    ["department", "Department"],
    ["employmentType", "Employment type"],
    ["location", "Location"],
    ["reportingManagerName", "Reporting Manager"],
    ["reportingManagerDesignation", "Manager Designation"],
  ],
  OTHER: [
    ["credentialId", "Credential ID"],
    ["url", "Verification URL"],
  ],
};

function metaLine(type, metadata) {
  if (!metadata || typeof metadata !== "object") return "";
  return (RESUME_DETAILS[type] ?? [])
    .filter(([key]) => ![null, undefined, ""].includes(metadata[key]))
    .map(([key, label]) => `${label}: ${metadata[key]}`)
    .join("  |  ");
}

function entryHtml(c) {
  const meta = metaLine(c.type, c.metadata);
  const isVerified = c.status === "VERIFIED";
  return `
    <div class="entry">
      <div class="entry-head">
        <div class="entry-title">${esc(c.title)}${isVerified ? '<span class="verified">Verified</span>' : ""}</div>
        <div class="entry-dates">${esc(dateRange(c))}</div>
      </div>
      ${c.issuer ? `<div class="entry-issuer">${esc(c.issuer)}</div>` : ""}
      ${meta ? `<div class="entry-meta">${esc(meta)}</div>` : ""}
      ${c.description ? `<div class="entry-desc">${esc(c.description)}</div>` : ""}
    </div>`;
}

function sectionHtml(label, items) {
  if (items.length === 0) return "";
  return `
    <section class="section">
      <h2 class="section-title">${esc(label)}</h2>
      ${items.map(entryHtml).join("")}
    </section>`;
}

/**
 * Build the standalone resume HTML document for a professional.
 * Shows verified credentials, or all available credentials if none are verified yet.
 */
export function buildResumeHtml({
  name,
  role,
  location,
  email,
  bio,
  credentials,
}) {
  const verified = (credentials ?? []).filter((c) => c.status === "VERIFIED");
  const items = verified.length > 0 ? verified : (credentials ?? []);
  const sections = [
    sectionHtml(
      TYPE_LABEL.EDUCATION,
      items.filter((c) => c.type === "EDUCATION"),
    ),
    sectionHtml(
      TYPE_LABEL.WORK,
      items.filter((c) => c.type === "WORK"),
    ),
    sectionHtml(
      TYPE_LABEL.OTHER,
      items.filter((c) => c.type === "OTHER"),
    ),
  ].join("");

  const subtitle = [role, location].filter(Boolean).join(" | ");
  const generated = new Date().toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${esc(name)} - Resume</title>
<style>
  @page { margin: 16mm; }
  * { box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans", system-ui, sans-serif;
    color: #1a1a1a; margin: 0; padding: 24px; line-height: 1.45;
  }
  .sheet { max-width: 760px; margin: 0 auto; }
  header { border-bottom: 2px solid #08833E; padding-bottom: 12px; margin-bottom: 16px; }
  h1 { font-size: 26px; margin: 0 0 4px; letter-spacing: -0.4px; }
  .subtitle { font-size: 13px; color: #555; }
  .contact { font-size: 12px; color: #08833E; margin-top: 4px; }
  .bio { font-size: 13px; color: #333; margin: 12px 0 0; }
  .section { margin-top: 18px; }
  .section-title {
    font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px;
    color: #08833E; margin: 0 0 8px; padding-bottom: 4px; border-bottom: 1px solid #e3e3e3;
  }
  .entry { margin-bottom: 12px; page-break-inside: avoid; }
  .entry-head { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; }
  .entry-title { font-size: 14px; font-weight: 700; }
  .verified {
    display: inline-block; margin-left: 8px;
    font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px;
    color: #057642; background: #DCF5E7; padding: 1px 7px; border-radius: 10px; vertical-align: middle;
  }
  .entry-dates { font-size: 12px; color: #777; white-space: nowrap; }
  .entry-issuer { font-size: 13px; color: #444; }
  .entry-meta { font-size: 12px; color: #555; margin-top: 2px; }
  .entry-desc { font-size: 12px; color: #444; margin-top: 4px; white-space: pre-line; }
  footer { margin-top: 22px; padding-top: 10px; border-top: 1px solid #e3e3e3; font-size: 10px; color: #999; }
  .empty { font-size: 13px; color: #777; margin-top: 16px; }
</style>
</head>
<body onload="window.focus(); window.print();">
  <div class="sheet">
    <header>
      <h1>${esc(name)}</h1>
      ${subtitle ? `<div class="subtitle">${esc(subtitle)}</div>` : ""}
      ${email ? `<div class="contact">${esc(email)}</div>` : ""}
      ${bio ? `<p class="bio">${esc(bio)}</p>` : ""}
    </header>
    ${sections || `<div class="empty">No verified credentials yet.</div>`}
    <footer>
      Generated by TickAndPass on ${esc(generated)}.${verified.length > 0 ? " All listed credentials are independently verified." : ""}
    </footer>
  </div>
</body>
</html>`;
}

/** Open the resume in a new window and trigger the print / Save-as-PDF dialog. */
export function downloadResume(data) {
  const html = buildResumeHtml(data);
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  if (!win) {
    // Popup blocked: fallback to hidden iframe so printing still works seamlessly
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch {
        /* fallback */
      }
      setTimeout(() => {
        try {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(url);
        } catch {
          /* ignore */
        }
      }, 2000);
    };
    iframe.src = url;
    return true;
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return true;
}
